/**
 * When a scheduled nudge will ACTUALLY go out, phrased for staff.
 *
 * The conversation's `nextNudgeAt` is when the nudge becomes *due*, not when it
 * sends: the recovery sweep defers anything outside the clinic's quiet-hours
 * window to the next open one. So a nudge due at 11:14 PM sends at 8:00 AM, and
 * a drawer that prints "at 11:14 PM · quiet hours respected" contradicts itself
 * on the same line — the exact class of detail that teaches staff not to trust
 * the screen.
 *
 * Everything here works in CLINIC-LOCAL wall-clock via `Intl`, never in the
 * browser's zone: a Portland front desk and a New York admin looking at the same
 * case must read the same send time, because it's the clinic's window that
 * governs. Deliberately no UTC arithmetic — we only ever need to render a local
 * time, so we read local parts and reason about them directly.
 *
 * Pure and clock-injected, so the boundary cases (late evening, pre-dawn,
 * rollover to tomorrow) are unit-tested without waiting for 11 PM.
 */
import { QUIET_HOURS_START, QUIET_HOURS_END } from '../../shared/smsNudge'

export interface QuietHours {
  startHour: number
  endHour: number
}

export const DEFAULT_QUIET_HOURS: QuietHours = {
  startHour: QUIET_HOURS_START,
  endHour: QUIET_HOURS_END,
}

interface LocalParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
}

/** An instant's wall-clock parts in a given IANA zone. */
function localParts(at: Date, timeZone: string): LocalParts | null {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })
    const got: Record<string, string> = {}
    for (const p of fmt.formatToParts(at)) {
      if (p.type !== 'literal') got[p.type] = p.value
    }
    return {
      year: Number(got.year),
      month: Number(got.month),
      day: Number(got.day),
      // Intl renders midnight as "24" in some engines under hour12:false.
      hour: Number(got.hour) % 24,
      minute: Number(got.minute),
    }
  } catch {
    // An invalid/unknown timezone must not take the drawer down — the caller
    // falls back to a time-free phrasing.
    return null
  }
}

/** Days between two local calendar dates (b − a), ignoring clock time. */
function dayDelta(a: LocalParts, b: LocalParts): number {
  const ms = Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)
  return Math.round(ms / 86_400_000)
}

/** "8:00 AM" / "11:14 PM" from a 24h hour + minute. */
function clock(hour: number, minute: number): string {
  const suffix = hour < 12 ? 'AM' : 'PM'
  const h12 = hour % 12 === 0 ? 12 : hour % 12
  return `${h12}:${String(minute).padStart(2, '0')} ${suffix}`
}

function inWindow(hour: number, q: QuietHours): boolean {
  return hour >= q.startHour && hour < q.endHour
}

/**
 * The staff-facing phrase for when the next nudge goes out — e.g. `"11:30 AM"`,
 * `"8:00 AM tomorrow"`, `"8:00 AM on Jul 23"`. Returns null when we can't say
 * honestly (bad timestamp, unknown zone), and the caller drops the time rather
 * than guessing.
 *
 * A due time outside the window is moved forward to the window opening: same
 * day if it's still before opening, otherwise the next day — which is exactly
 * what the sweep does when it defers.
 */
export function formatNudgeSendTime(
  nextNudgeAt: string,
  timeZone: string,
  quietHours: QuietHours = DEFAULT_QUIET_HOURS,
  now: Date = new Date(),
): string | null {
  const due = new Date(nextNudgeAt)
  if (!Number.isFinite(due.getTime())) return null

  const q = normalizeQuietHours(quietHours)
  const dueLocal = localParts(due, timeZone)
  const nowLocal = localParts(now, timeZone)
  if (!dueLocal || !nowLocal) return null

  // Where it lands after the sweep's deferral.
  let sendHour = dueLocal.hour
  let sendMinute = dueLocal.minute
  let sendDayOffset = 0
  if (!inWindow(dueLocal.hour, q)) {
    sendHour = q.startHour
    sendMinute = 0
    // Past closing → the window opens tomorrow. Before opening → today.
    if (dueLocal.hour >= q.endHour) sendDayOffset = 1
  }

  const daysOut = dayDelta(nowLocal, dueLocal) + sendDayOffset
  const time = clock(sendHour, sendMinute)
  if (daysOut <= 0) return time
  if (daysOut === 1) return `${time} tomorrow`

  const onDay = new Date(Date.UTC(dueLocal.year, dueLocal.month - 1, dueLocal.day + sendDayOffset))
  const label = onDay.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  })
  return `${time} on ${label}`
}

/**
 * A clinic's window, guarded. A malformed/inverted override (a hand-edited doc,
 * or a 24h "always on" pair) falls back to the global default rather than
 * producing a window nothing can ever land in.
 */
export function normalizeQuietHours(q: QuietHours | null | undefined): QuietHours {
  if (!q) return DEFAULT_QUIET_HOURS
  const { startHour, endHour } = q
  const valid = Number.isInteger(startHour) && Number.isInteger(endHour) &&
    startHour >= 0 && startHour <= 23 &&
    endHour >= 1 && endHour <= 24 &&
    startHour < endHour
  return valid ? { startHour, endHour } : DEFAULT_QUIET_HOURS
}
